/** The email-OTP auth endpoints. Pure API calls — DOM-free, so the signin
 * flow, the widget, and any future page can share them. Each call either
 * returns the parsed payload or throws an Error whose message is safe to
 * show the user.
 */

import type { Client } from "./client";
import { ApiHttpError, apiErrorMessage, errorBody } from "./errors";
import type { ApiError, SeekrUser, VerifyResponse } from "./types";

/** Ask seekr to email a 6-digit code. request-code is enumeration-safe
 * (always 200 when accepted), so a non-2xx means throttled (429) or a bad
 * email (400) — callers must not advance to the code step as if a code were
 * sent. Either way seekr's own wording is the only thing that says which, so
 * it is what the visitor is shown. */
export async function requestCode(client: Client, email: string): Promise<void> {
  const res = await client.post(`${client.authBase}/email-otp/request-code/`, {
    email,
  });
  if (!res.ok) {
    const message = apiErrorMessage(await errorBody(res));
    throw new Error(message || "Couldn't send a code. Please try again.");
  }
}

export async function verifyCode(
  client: Client,
  email: string,
  code: string,
): Promise<VerifyResponse> {
  const res = await client.post(`${client.authBase}/email-otp/verify-code/`, {
    email,
    code,
  });
  const data = (await res.json()) as VerifyResponse;
  // Fallback is user-facing, so keep it friendly.
  if (!res.ok) {
    throw new Error(
      apiErrorMessage(data) || "That code is invalid or has expired.",
    );
  }
  return data;
}

export async function completeSignup(
  client: Client,
  details: {
    verificationToken: string;
    firstName: string;
    lastName: string;
    referralCode: string;
    /** Attribution, stored verbatim on the seekr user profile; omitted from
     * the payload when undefined. */
    source?: string;
  },
): Promise<SeekrUser> {
  const res = await client.post(
    `${client.authBase}/email-otp/complete-signup/`,
    {
      verification_token: details.verificationToken,
      first_name: details.firstName,
      last_name: details.lastName,
      referral_code: details.referralCode,
      ...(details.source ? { source: details.source } : {}),
    },
  );
  const data = (await res.json()) as SeekrUser & ApiError;
  if (!res.ok) {
    throw new ApiHttpError(res.status, apiErrorMessage(data) || "signup failed");
  }
  return data;
}

/** The single answer to every rejected password.
 *
 * seekr's legacy endpoint distinguishes "User not found" from "Password not
 * recognised", both as 401s. Passing that through would make the fallback an
 * account-existence oracle on a flow whose OTP half is deliberately
 * enumeration-safe (request-code always 200s), so both collapse to this.
 *
 * It also carries the only steer we can give a social-only account: those have
 * no usable password, so they always land on a 401 with nothing to correct,
 * and we can't detect them without asking seekr a question that leaks. */
const PASSWORD_REJECTED =
  "That email address and password weren't recognised. If you signed up with Google or Facebook, use those buttons instead.";

/** Sign in with an email and password — the fallback for a visitor whose
 * emailed code never arrives. Proxied to seekr's legacy login, which sets the
 * same session the OTP path does.
 *
 * Resolves to the signed-in user, or `null` when seekr reports a session
 * already exists (409): that answer means the visitor *is* signed in — most
 * likely in another tab — so the flow completes rather than stranding them on
 * an error they can't act on. */
export async function signInWithPassword(
  client: Client,
  email: string,
  password: string,
): Promise<SeekrUser | null> {
  const res = await client.post(client.passwordLoginUrl, { email, password });
  if (res.status === 409) return null;
  // The body is deliberately not read on a rejection — see PASSWORD_REJECTED.
  if (res.status === 400 || res.status === 401) throw new Error(PASSWORD_REJECTED);
  if (!res.ok) throw new Error("Couldn't sign you in. Please try again.");
  return (await res.json()) as SeekrUser;
}

/** End the seekr session (proxied to Django's LogoutView, hence the CSRF'd
 * POST the client provides). */
export async function logout(client: Client): Promise<void> {
  const res = await client.post(client.logoutUrl);
  if (!res.ok) throw new Error(`logout failed (${res.status})`);
}

/** Who the visitor's cookies say they are, or null when nobody.
 *
 * Read-only and best-effort: any failure — offline, 5xx, an unparseable body —
 * resolves to null rather than throwing. A host page uses this to render its
 * signed-in state, and a broken read there should look like "not signed in",
 * not take the page down.
 *
 * seekr wraps the answer: `{ "user": {...} }` for a live session, and a `user` of
 * null (or absent) for an anonymous one — it answers 200 either way rather than
 * 401, so the wrapper is the only signal. Reading the body as the user itself
 * silently reports every signed-in visitor as anonymous.
 */
export async function fetchCurrentUser(client: Client): Promise<SeekrUser | null> {
  try {
    const res = await client.get(client.currentUserUrl);
    if (!res.ok) return null;
    const body = (await res.json()) as { user?: SeekrUser | null } | null;
    const user = body?.user;
    return user && user.id !== undefined ? user : null;
  } catch {
    return null;
  }
}
